/**
 * Pure utility functions — no side effects, no imports.
 * All are independently testable.
 */

/** Sanitises a string for use as a Firebase key */
export const fk = s => (s || '').replace(/[^a-zA-Z0-9]/g, '_');

/** Pads a number to two digits */
export const pad = n => String(n).padStart(2, '0');

/** Formats total seconds as MM:SS */
export const formatTime = secs => `${pad(Math.floor(secs / 60))}:${pad(secs % 60)}`;

/**
 * Human-facing label combining a team's canonical station identity (e.g.
 * "Team 1" — used for every Firebase lookup key) with the custom name they
 * registered (e.g. "Bankers"). Falls back to the canonical name alone when
 * no display name has been recorded yet (e.g. mid-registration, or legacy data).
 * @param {string} canonical
 * @param {string|undefined|null} displayName
 */
export const teamLabel = (canonical, displayName) =>
  displayName ? `${displayName} (${canonical})` : canonical;

/** Escapes user content for safe innerHTML insertion */
export const escHtml = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

/**
 * The single progressive standing badge shown everywhere a team's licence
 * position appears (Team view, Licence Board, debrief). Splits the points
 * earned so far into thirds of the maximum achievable *from the rounds
 * scored so far* — e.g. after Round 1 alone (max 12) a score of 9 sits in
 * the top third, but once Round 2 lands (max 27) that same team is
 * re-measured against the new ceiling, then again out of the full 42 once
 * Round 3 lands. The rule never changes basis, so a team's reading only
 * ever reflects one verdict — it can't show two disagreeing labels at once.
 * Once every round is scored (`complete`), the wording switches from interim
 * ("Full") to final licence wording ("Full Operating Licence") to signal the
 * result is now locked in — same tier, same colour, just the final framing.
 * @param {number} pts
 * @param {number} max
 * @param {boolean} complete  whether every round has been scored — final vs interim wording
 * @returns {{ label: string, cls: string, tier: 'full'|'conditional'|'review' }}
 */
export function licenceTier(pts, max, complete) {
  if (!max) return { label: 'Under Review', cls: 'licence-badge--review', tier: 'review' };
  const low  = Math.floor(max / 3);
  const high = Math.floor((2 * max) / 3);
  const tier = pts > high ? 'full' : pts > low ? 'conditional' : 'review';
  const label = complete
    ? { full: 'Full Operating Licence', conditional: 'Conditional Licence', review: 'Under Review' }[tier]
    : { full: 'Full',                   conditional: 'Conditional',         review: 'Under Review' }[tier];
  return { label, cls: `licence-badge--${tier}`, tier };
}

/**
 * The maximum achievable points from rounds that have recorded scores for a
 * team, and whether every round has been scored. The other half of the pair
 * passed to `licenceTier()` alongside `teamTotal()` — keeping this derivation
 * in one place is what guarantees every view agrees on a team's standing.
 * @param {Record<string, number[]>} allScores
 * @param {string} teamKey
 * @param {{ max: number }[]} rounds
 * @returns {{ max: number, complete: boolean }}
 */
export function scoredProgress(allScores, teamKey, rounds) {
  let max = 0, scored = 0;
  rounds.forEach((R, r) => {
    if (Array.isArray(allScores?.[`${teamKey}_R${r}`])) { max += roundMax(R); scored++; }
  });
  return { max, complete: scored === rounds.length };
}

/**
 * Sums a score array.
 * @param {number[]} scores
 * @returns {number}
 */
export function roundTotal(scores) {
  if (!Array.isArray(scores)) return 0;
  return scores.reduce((acc, v) => acc + (Number(v) || 0), 0);
}

/**
 * Computes a team's total score across all rounds.
 * @param {Record<string, number[]>} allScores  scores map from game state
 * @param {string} teamKey  fk(teamName)
 * @param {number} roundCount
 * @returns {number}
 */
export function teamTotal(allScores, teamKey, roundCount = 3) {
  let total = 0;
  for (let r = 0; r < roundCount; r++) {
    total += roundTotal(allScores?.[`${teamKey}_R${r}`]);
  }
  return total;
}

/**
 * Returns the expected score-array length for a round.
 * dims.length + 1 (bomb dim).
 * @param {{ dims: string[] }} round
 */
export const scoreArrayLen = round => round.dims.length + 1;

/**
 * Returns the maximum possible score for a round (max property + 3 for bomb).
 * @param {{ max: number }} round
 */
export const roundMax = round => round.max + 3;

/** Clamps a value between min and max (inclusive). */
export const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

/**
 * Alphabet for session join codes — unambiguous when read aloud or written
 * on a whiteboard: no I/L/O/0/1.
 */
export const SESSION_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const SESSION_CODE_LENGTH = 6;

/**
 * Generates a random session join code, e.g. "K7MPX3".
 * ~30^6 ≈ 700M combinations — collisions are handled by the create
 * transaction (firebase.createSession), not by uniqueness here.
 */
export function generateSessionCode(len = SESSION_CODE_LENGTH) {
  let out = '';
  const rand = new Uint32Array(len);
  crypto.getRandomValues(rand);
  for (let i = 0; i < len; i++) out += SESSION_CODE_ALPHABET[rand[i] % SESSION_CODE_ALPHABET.length];
  return out;
}

/**
 * Normalises user-typed session codes: uppercase, strips spaces and dashes.
 * Returns '' when the result isn't a plausible code (wrong length, or
 * contains characters the alphabet deliberately avoids — I/L/O/0/1 can
 * never appear in a valid code).
 * @param {string} raw
 */
export function normaliseSessionCode(raw) {
  const code = String(raw ?? '').toUpperCase().replace(/[\s-]/g, '');
  return new RegExp(`^[${SESSION_CODE_ALPHABET}]{${SESSION_CODE_LENGTH}}$`).test(code) ? code : '';
}
